import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { ParcelData } from '../types';
import { getPolygonStyle, getPolygonHoverStyle, getPolygonSelectedStyle } from '../../../utils/colors/parcelStatusUtils';

interface LayerVisibility {
  land_parcels: boolean;
}

interface LandParcelLayerProps {
  parcels?: ParcelData[];
  layerVisibility: LayerVisibility;
  onParcelUpdate?: (updatedParcel: ParcelData) => void;
  /** Klick auf ein Flurstück; `latlng` ist die Klickstelle, etwa für ein Popup dort */
  onParcelClick?: (parcelId: string, latlng: L.LatLng) => void;
  selectedParcelId?: string;
}

/** Nicht-relevante Flurstücke werden mit stark reduzierter Opazität dargestellt */
const IRRELEVANT_OPACITY_FACTOR = 0.2;

const dimStyle = (style: L.PathOptions): L.PathOptions => ({
  ...style,
  opacity: (style.opacity ?? 1) * IRRELEVANT_OPACITY_FACTOR,
  fillOpacity: (style.fillOpacity ?? 0.3) * IRRELEVANT_OPACITY_FACTOR,
});

function computeStyle(parcel: ParcelData, selectedParcelId?: string): L.PathOptions {
  const isSelected = parcel.id === selectedParcelId;
  const isRelevant = parcel.relevant !== false;
  const base = isSelected ? getPolygonSelectedStyle(parcel.status) : getPolygonStyle(parcel.status);
  return isRelevant ? base : dimStyle(base);
}

/**
 * Rendert Flurstücke als Polygone auf der Karte.
 * Verwendet eine Registry (useRef), um bestehende Polygone bei Änderungen von
 * `relevant`/`status`/`selectedParcelId` nur per setStyle() zu aktualisieren
 * statt sie komplett neu zu erstellen → kein Flackern.
 */
const LandParcelLayer: React.FC<LandParcelLayerProps> = ({
  parcels,
  layerVisibility,
  onParcelUpdate,
  onParcelClick,
  selectedParcelId,
}) => {
  const map = useMap();
  const [updatedParcels, setUpdatedParcels] = useState<ParcelData[]>([]);

  const registryRef = useRef<Map<string, L.Polygon[]>>(new Map());
  const parcelsRef = useRef<ParcelData[]>([]);
  const selectedRef = useRef<string | undefined>(undefined);
  const onParcelClickRef = useRef(onParcelClick);

  parcelsRef.current = updatedParcels;
  selectedRef.current = selectedParcelId;
  onParcelClickRef.current = onParcelClick;

  useEffect(() => {
    setUpdatedParcels(parcels || []);
  }, [parcels]);

  const handleParcelUpdate = useCallback((updatedParcel: ParcelData) => {
    setUpdatedParcels(prev =>
      prev.map(f => f.id === updatedParcel.id ? updatedParcel : f)
    );
    onParcelUpdate?.(updatedParcel);
  }, [onParcelUpdate]);

  useEffect(() => {
    return () => {
      registryRef.current.forEach(polys => polys.forEach(p => map.removeLayer(p)));
      registryRef.current.clear();
    };
  }, [map]);

  useEffect(() => {
    const registry = registryRef.current;

    if (!updatedParcels?.length || !layerVisibility.land_parcels) {
      registry.forEach(polys => polys.forEach(p => map.removeLayer(p)));
      registry.clear();
      return;
    }

    const currentIds = new Set(updatedParcels.map(p => p.id));

    for (const [id, polys] of registry) {
      if (!currentIds.has(id)) {
        polys.forEach(p => map.removeLayer(p));
        registry.delete(id);
      }
    }

    updatedParcels.forEach((parcel) => {
      const existing = registry.get(parcel.id);

      if (existing) {
        existing.forEach(p => p.setStyle(computeStyle(parcel, selectedParcelId)));
        return;
      }

      const { geometrie } = parcel;
      if (!geometrie || !geometrie.type) return;

      const createPolygon = (coords: number[][][]): L.Polygon => {
        const leafletCoords = coords.map(ring =>
          ring.map((coord: number[]) => [coord[1], coord[0]] as [number, number])
        );

        const polygon = L.polygon(leafletCoords, {
          ...computeStyle(parcel, selectedParcelId),
          pane: 'tilePane',
        });

        polygon.on('click', (e: L.LeafletMouseEvent) => onParcelClickRef.current?.(parcel.id, e.latlng));
        polygon.on('mouseover', () => {
          const current = parcelsRef.current.find(p => p.id === parcel.id);
          if (!current || parcel.id === selectedRef.current) return;
          const isRelevant = current.relevant !== false;
          const hover = getPolygonHoverStyle(current.status);
          polygon.setStyle(isRelevant ? hover : dimStyle(hover));
        });
        polygon.on('mouseout', () => {
          const current = parcelsRef.current.find(p => p.id === parcel.id);
          if (!current || parcel.id === selectedRef.current) return;
          polygon.setStyle(computeStyle(current, selectedRef.current));
        });

        polygon.addTo(map);
        return polygon;
      };

      const newPolygons: L.Polygon[] = [];

      if (geometrie.type === 'Polygon') {
        newPolygons.push(createPolygon(geometrie.coordinates as number[][][]));
      } else if (geometrie.type === 'MultiPolygon') {
        (geometrie.coordinates as number[][][][]).forEach((poly: number[][][]) => {
          newPolygons.push(createPolygon(poly));
        });
      }

      if (newPolygons.length > 0) {
        registry.set(parcel.id, newPolygons);
      }
    });
  }, [updatedParcels, layerVisibility.land_parcels, map, selectedParcelId]);

  return null;
};

export default LandParcelLayer;
